import { useContext, useEffect, useRef, useState } from "react";
import { Box, GlobalStyles, Grid, Typography } from "@mui/material";
import Back from "../../../common/components/BackNavigateComponent";
import ZeroSalesOverview from "./ZeroSalesOverview";
import ZeroSalesToolTip from "./components/ZeroSalesToolTip";
import CompanyFilter from "./components/CompanyFilter";
import NumOfDaysFilter from "./components/NumOfDaysFilter";
import AsOfDate from "./components/AsOfDate";
import { WindowSizeContext } from "../../../common/context/WindowSizeContext";
import { STORE_ZERO_SALES_TABLE, useAvailableDateRange } from "./hooks";
import moment from "moment";
import StoreArticleZeroSales from "./StoreArticleZeroSales";
import { registerDashboard } from "../../../common/utils";
import { dashboardDefinition } from "./dashboardDefinition";
import DashboardMetaProvider from "../common/providers/DashboardMetaProvider";
import ArticleTypeFilter from "./components/ArticleTypeFilter";
import ToggleProvider from "./provider/toggleProvider";
import { useToggleContext } from "../Inventory/provider/context";
import { toast } from "react-toastify";

registerDashboard(dashboardDefinition)

const ToggleOptions = [
    { label: 'Must-Have', value: 'must_have' },
    { label: 'Innovation Product', value: 'innovation_product' },
]

const ObservationPeriodCondition = () => {
    toast.info(
        <Box>
            <Typography variant="body2" fontSize="small" fontWeight="bold">
                No. of Days: 15 Days
            </Typography>
            <Typography variant="body2" fontSize="small" sx={{ mt: 0.5 }}>
                Includes items with <strong>12–15 zero-sales days</strong> in the <strong>last 15 days, consecutive or non-consecutive</strong>.
            </Typography>
        </Box>,
        { autoClose: 15000 }
    );
};

export default function ZeroSales() {
    const { data: dateRangeData, isLoading: isDateRangeLoading } = useAvailableDateRange();
    const windowSize = useContext(WindowSizeContext)
    const [filters, setFilters] = useState({
        company_id: [2, 7, 142],
        days: 3,
        startDate: null,
        endDate: null,
        article_type: 'ZTRA',
        article_sub_category: ["4k", "888"]
    });
    const [selectedStore, setSelectedStore] = useState(null)

    const [overviewTableIsEmpty, setOverviewTableIsEmpty] = useState(null)

    useEffect(() => {
        if (dateRangeData?.max && filters?.days) {
            setFilters(prevFilters => ({
                ...prevFilters,
                startDate: moment(dateRangeData.max).subtract(prevFilters?.days - 1, 'days').format('YYYY-MM-DD'),
                endDate: moment(dateRangeData.max).format('YYYY-MM-DD'),
            }));
        }
    }, [dateRangeData, filters?.days]);

    useEffect(() => {
        ObservationPeriodCondition()
    }, []);

    function onDropdownFilterChange(value, field) {
        setFilters({
            ...filters,
            [field]: value
        })
    }

    const handleRadioChange = (storeId, storeName) => {
        setFilters(prevState => {
            if (filters?.store_id && prevState?.store_id === storeId) {
                onDropdownFilterChange(null, 'store_id')
                setSelectedStore(null)
                return {
                    ...prevState,
                    store_id: null
                }
            } else {
                onDropdownFilterChange(storeId, 'store_id')
                setSelectedStore(storeName)
                return {
                    ...prevState,
                    store_id: storeId
                }
            }
        });
    };

    const coverageStartDate = dateRangeData?.max
        ? moment(dateRangeData.max).subtract(filters.days - 1, 'days').format('MMMM DD, YYYY')
        : '---';
    const coverageEndDate = dateRangeData?.max
        ? moment(dateRangeData.max).format('MMMM DD, YYYY')
        : '---';


    const defaultInformationContent = (
        <Box paddingY={2}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                No of Days:
            </Typography>
            <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: 20 }}>
                <li>
                    <Typography variant="body2" component="span">
                        Counts consecutive zero-sales days in intervals of 3, 7, 15* or 30 days.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body2" component="span">
                        Includes items with <strong>12–15 zero-sales days</strong> in the <strong>last 15 days, consecutive or non-consecutive</strong>.
                    </Typography>
                </li>
            </ul>
        </Box>
    );

    return <DashboardMetaProvider dashboardDefinition={dashboardDefinition}>
        <GlobalStyles
            styles={{
                body: { backgroundColor: '#e0e0e0' },

                ".Toastify__toast-container--top-right": {
                    width: "425px",
                    right: "16px"
                },

                ".Toastify__toast-container--top-right .Toastify__toast": {
                    width: "100%"
                }
            }}
        />
        <Grid container xs={12}>

            <Grid item xs={12} sx={{ display: !windowSize.isOnMobile && "flex", justifyContent: "space-between", alignItems: "center", my: 2 }}>
                <Grid item xs display="flex" sx={{ alignItems: "center" }}>
                    <Back />
                    <Typography variant="h5" ml={3}> Zero Sales </Typography>
                </Grid>
                <Grid item xs>
                    <AsOfDate />
                </Grid>

            </Grid>

            <Grid container >
                <Box flexGrow={1} xs={12} mb={1} sx={{ backgroundColor: '#f6f6f8', border: "1px solid #e0e0e0", borderRadius: ".5rem" }}>
                    <Grid container columnSpacing={1} rowSpacing={windowSize.isOnMobile ? 1.5 : .5} padding={1} paddingX={windowSize.isOnMobile && 1}>
                        <Grid item xs={12} sm={5} mx={1} >
                            <CompanyFilter
                                tableName={STORE_ZERO_SALES_TABLE}
                                filters={filters}
                                onPillSelectChange={onDropdownFilterChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm mx={1}>
                            <NumOfDaysFilter
                                filters={filters}
                                onFilterChange={onDropdownFilterChange}
                                isLoading={isDateRangeLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm mx={1}>
                            <ArticleTypeFilter
                                filters={filters}
                                onFilterChange={onDropdownFilterChange}
                                isLoading={isDateRangeLoading} />
                        </Grid>
                        <Grid item xs={12} sm={3.5} mx={1} gap={1} sx={{ display: !windowSize.isOnMobile && 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Typography sx={{ fontWeight: "bold" }}> Coverage Data: </Typography>
                            <Typography sx={{ textDecoration: "underline" }}>
                                {isDateRangeLoading ? 'Loading...' : `${coverageStartDate} - ${coverageEndDate}`}
                            </Typography>
                            <ZeroSalesToolTip informationContent={defaultInformationContent} />
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
            <ToggleProvider>
                <ZeroSalesOverviewWrapper filters={filters} handleRadioChange={handleRadioChange} setOverviewTableIsEmpty={setOverviewTableIsEmpty} ToggleOptions={ToggleOptions} />
                <StoreArticleZeroSalesWrapper filters={filters} onFilterChange={setFilters} selectedStore={selectedStore} overviewTableIsEmpty={overviewTableIsEmpty} />
            </ToggleProvider>
        </Grid >
    </DashboardMetaProvider>

}

function ZeroSalesOverviewWrapper({ filters, handleRadioChange, setOverviewTableIsEmpty, ToggleOptions }) {
    const { toggleState, updateToggleState } = useToggleContext();
    return (
        <ZeroSalesOverview
            filters={filters}
            handleRadioChange={handleRadioChange}
            setOverviewTableIsEmpty={setOverviewTableIsEmpty}
            ToggleOptions={ToggleOptions}
            updateToggleState={updateToggleState}
            toggleState={toggleState}
        />
    );
}

function StoreArticleZeroSalesWrapper({ filters, onFilterChange, selectedStore, overviewTableIsEmpty }) {
    const { toggleState } = useToggleContext();
    const DEFAULT_SUB_CATEGORY = {
        must_have: ['888', '4k'],
        innovation_product: ['SM Eats', 'Food To Go']
    };
    const prevToggleRef = useRef(toggleState);

    useEffect(() => {
        if (typeof onFilterChange === 'function') {
            onFilterChange(prev => {
                const defaultValue = DEFAULT_SUB_CATEGORY[toggleState];

                const isEmpty =
                    prev?.article_sub_category === undefined ||
                    prev?.article_sub_category === null ||
                    prev?.article_sub_category?.length === 0 ||
                    (Array.isArray(prev.article_sub_category) &&
                        prev.article_sub_category.every(v => !v));

                if (isEmpty || prevToggleRef.current !== toggleState) {
                    return {
                        ...prev,
                        article_sub_category: defaultValue
                    };
                }

                return prev; // keep user selection
            });
        }

        prevToggleRef.current = toggleState;
    }, [toggleState, onFilterChange, filters.article_sub_category]);

    return (
        <StoreArticleZeroSales
            filters={filters}
            onFilterChange={onFilterChange}
            selectedStore={selectedStore}
            overviewTableIsEmpty={overviewTableIsEmpty}
            toggleState={toggleState}
        />
    );
}